import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  IconButton,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import { apiClient } from '../../services/apiClient';
import { LoadingScreen } from '../../components/common/LoadingScreen';

interface AuditLogEntry {
  id: number;
  actor_uuid: string | null;
  actor_name: string | null;
  actor_email: string | null;
  action: string;
  resource_type: string;
  resource_id: string;
  target_user_uuid: string | null;
  target_organization_id: number | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

interface AuditLogListResponse {
  entries: AuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

const ACTIONS = ['grant', 'revoke', 'create', 'delete', 'update'];

const RESOURCE_TYPES = [
  'organisation_member',
  'email_domain_mapping',
  'organisation_location_access',
  'user_location_access',
  'location_group',
  'location_group_member',
  'organisation_location_group_access',
  'user_location_group_access',
  'feature_flag',
  'organisation_feature_flag',
  'user_feature_flag',
];

const actionChipColour = (action: string): 'success' | 'error' | 'warning' | 'default' => {
  if (action === 'grant' || action === 'create') return 'success';
  if (action === 'revoke' || action === 'delete') return 'error';
  if (action === 'update') return 'warning';
  return 'default';
};

const AuditLogPage: React.FC = () => {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [actionFilter, setActionFilter] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (actionFilter) params.action = actionFilter;
      if (resourceTypeFilter) params.resource_type = resourceTypeFilter;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        params.date_to = end.toISOString();
      }

      const data = await apiClient.get<AuditLogListResponse>('/admin/audit-log', { params });
      setEntries(data.entries);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, actionFilter, resourceTypeFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleFilterChange = () => {
    setPage(0);
  };

  if (loading && entries.length === 0) {
    return <LoadingScreen message="Loading audit log..." />;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HistoryIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Audit Log
            </Typography>
          </Box>
        </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filter bar */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select
          label="Action"
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); handleFilterChange(); }}
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 140 } }}
        >
          <MenuItem value="">All</MenuItem>
          {ACTIONS.map((a) => (
            <MenuItem key={a} value={a}>{a}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Resource Type"
          value={resourceTypeFilter}
          onChange={(e) => { setResourceTypeFilter(e.target.value); handleFilterChange(); }}
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 220 } }}
        >
          <MenuItem value="">All</MenuItem>
          {RESOURCE_TYPES.map((rt) => (
            <MenuItem key={rt} value={rt}>{rt}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="Date From"
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); handleFilterChange(); }}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 150 }, flex: { xs: 1, sm: 'none' } }}
        />

        <TextField
          label="Date To"
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); handleFilterChange(); }}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 150 }, flex: { xs: 1, sm: 'none' } }}
        />

        <IconButton onClick={fetchEntries} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date/Time</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Resource Type</TableCell>
              <TableCell>Resource ID</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    No audit log entries found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(entry.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {entry.actor_name || entry.actor_email || entry.actor_uuid || 'System'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.action}
                      color={actionChipColour(entry.action)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{entry.resource_type}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.resource_id}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.details ? JSON.stringify(entry.details) : ''}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>
      </Box>
    </Container>
  );
};

export default AuditLogPage;
